import { useCallback, useState } from 'preact/hooks';
import { ApiError, api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { ChangelogResponse } from '../lib/types.js';
import { formatTs } from '../lib/format.js';

export function Changelog() {
  const fetchLog = useCallback(() => api.get<ChangelogResponse>('/api/changelog'), []);
  const log = useFetch(fetchLog, []);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [feedbackIds, setFeedbackIds] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entries = [...(log.data?.items ?? [])].sort((a, b) => b.ts - a.ts);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const ids = feedbackIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => Number.isFinite(n));
      await api.post('/api/admin/changelog', { title, body, feedbackIds: ids });
      setTitle('');
      setBody('');
      setFeedbackIds('');
      log.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to publish entry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 class="page-title">Changelog</h1>
      {log.error && <div class="error-banner">{log.error}</div>}

      <div class="card" style={{ marginBottom: '18px' }}>
        <div class="section-label" style={{ marginTop: 0 }}>
          Compose entry
        </div>
        <form onSubmit={submit}>
          <div class="field">
            <label for="cl-title">Title</label>
            <input
              id="cl-title"
              class="input"
              required
              value={title}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label for="cl-body">Body</label>
            <textarea
              id="cl-body"
              class="textarea"
              required
              value={body}
              onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
            />
          </div>
          <div class="field">
            <label for="cl-ids">Feedback IDs (comma-separated, credits "you asked, we did")</label>
            <input
              id="cl-ids"
              class="input"
              placeholder="12, 47, 51"
              value={feedbackIds}
              onInput={(e) => setFeedbackIds((e.target as HTMLInputElement).value)}
            />
          </div>
          {error && <div class="error-banner">{error}</div>}
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {busy ? 'Publishing…' : 'Publish entry'}
          </button>
        </form>
      </div>

      {entries.length === 0 && !log.loading && <div class="empty">No changelog entries yet.</div>}
      {entries.map((e) => (
        <div class="card" key={e.id} style={{ marginBottom: '10px' }}>
          <div class="row" style={{ justifyContent: 'space-between' }}>
            <b>{e.title}</b>
            <span class="hint">{formatTs(e.ts)}</span>
          </div>
          <p style={{ margin: '8px 0 0', color: 'var(--bone-dim)', fontSize: '13px' }}>{e.body}</p>
          {e.feedbackIds.length > 0 && (
            <div class="hint" style={{ marginTop: '6px' }}>
              Credits feedback: {e.feedbackIds.map((id) => `#${id}`).join(', ')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
